import { supabase } from './supabase';
import { reduzirImagem } from './imagem';

// Comprovante de despesa (foto ou PDF) — fica no bucket PRIVADO 'comprovantes'.
// Nunca usamos URL pública: para ver, geramos uma URL assinada de curta duração.
export const BUCKET_COMPROVANTE = 'comprovantes';
export const LIMITE_COMPROVANTE_BYTES = 10 * 1024 * 1024; // 10 MB
const MAX_LADO_IMAGEM = 1600;

const TIPOS_ACEITOS = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  'application/pdf',
];

export function ehPdf(tipo: string | null | undefined): boolean {
  return (tipo ?? '').toLowerCase().includes('pdf');
}

// Nome seguro para o bucket: sem acento, sem espaço, só letras/números/-_.
function nomeLimpo(nome: string): string {
  const base = nome.replace(/\.[^./\\]+$/, '');
  const limpo = base
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^[-_.]+|[-_.]+$/g, '')
    .slice(0, 60);
  return limpo || 'comprovante';
}

function extensao(nome: string, tipo: string): string {
  const m = /\.([a-zA-Z0-9]{1,8})$/.exec(nome);
  if (m) return m[1].toLowerCase();
  if (ehPdf(tipo)) return 'pdf';
  if (tipo === 'image/png') return 'png';
  if (tipo === 'image/webp') return 'webp';
  return 'jpg';
}

function medirImagem(arquivo: File): Promise<{ largura: number; altura: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(arquivo);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ largura: img.naturalWidth, altura: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('imagem'));
    };
    img.src = url;
  });
}

// Reduz a imagem no navegador SÓ quando passa de 1600 px (JPEG 0,8).
// HEIC/HEIF nem sempre abre no canvas: se não der para medir, sobe como veio.
async function prepararImagem(
  arquivo: File,
): Promise<{ blob: Blob; tipo: string; ext: string }> {
  const original = {
    blob: arquivo as Blob,
    tipo: arquivo.type || 'image/jpeg',
    ext: extensao(arquivo.name, arquivo.type),
  };
  try {
    const { largura, altura } = await medirImagem(arquivo);
    if (Math.max(largura, altura) > MAX_LADO_IMAGEM) {
      const blob = await reduzirImagem(arquivo, MAX_LADO_IMAGEM, 0.8, 'image/jpeg');
      return { blob, tipo: 'image/jpeg', ext: 'jpg' };
    }
  } catch {
    return original;
  }
  return original;
}

export interface ComprovanteSalvo {
  path: string;
  nome: string;
  tipo: string;
}

// Sobe o arquivo e grava o caminho na despesa. Usado tanto ao anexar quanto ao trocar.
export async function enviarComprovante(params: {
  eventoId: number;
  despesaId: number;
  arquivo: File;
  pathAntigo?: string | null;
}): Promise<ComprovanteSalvo> {
  const { eventoId, despesaId, arquivo, pathAntigo } = params;

  if (arquivo.size > LIMITE_COMPROVANTE_BYTES) {
    throw new Error('O arquivo passa de 10 MB. Envie uma foto ou um PDF menor.');
  }
  if (arquivo.type && !TIPOS_ACEITOS.includes(arquivo.type.toLowerCase())) {
    throw new Error('Formato não aceito. Envie uma foto (JPG ou PNG) ou um PDF.');
  }

  const pdf = ehPdf(arquivo.type) || /\.pdf$/i.test(arquivo.name);
  let corpo: Blob = arquivo;
  let tipoFinal = arquivo.type || (pdf ? 'application/pdf' : 'image/jpeg');
  let ext = extensao(arquivo.name, arquivo.type);

  if (!pdf) {
    const preparado = await prepararImagem(arquivo);
    corpo = preparado.blob;
    tipoFinal = preparado.tipo;
    ext = preparado.ext;
  }

  const nomeFinal = `${Date.now()}-${nomeLimpo(arquivo.name)}.${ext}`;
  const path = `${eventoId}/${despesaId}/${nomeFinal}`;

  const { error: errUpload } = await supabase.storage
    .from(BUCKET_COMPROVANTE)
    .upload(path, corpo, { contentType: tipoFinal, upsert: false });
  if (errUpload) throw errUpload;

  const { error: errDb } = await supabase
    .from('despesas')
    .update({
      comprovante_path: path,
      comprovante_nome: arquivo.name.slice(0, 200),
      comprovante_tipo: tipoFinal,
    })
    .eq('id', despesaId);
  if (errDb) {
    // Não deixa arquivo órfão no bucket se a despesa não aceitou o caminho.
    await supabase.storage.from(BUCKET_COMPROVANTE).remove([path]);
    throw errDb;
  }

  if (pathAntigo && pathAntigo !== path) {
    await supabase.storage.from(BUCKET_COMPROVANTE).remove([pathAntigo]);
  }

  return { path, nome: arquivo.name, tipo: tipoFinal };
}

// URL assinada de curta duração (300 s) para ver o arquivo.
export async function urlComprovante(path: string, segundos = 300): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from(BUCKET_COMPROVANTE)
    .createSignedUrl(path, segundos);
  if (error) throw error;
  return data?.signedUrl ?? null;
}

// Apaga o arquivo do bucket e limpa as 3 colunas na despesa.
export async function removerComprovante(despesaId: number, path: string | null): Promise<void> {
  const { error: errDb } = await supabase
    .from('despesas')
    .update({ comprovante_path: null, comprovante_nome: null, comprovante_tipo: null })
    .eq('id', despesaId);
  if (errDb) throw errDb;
  if (path) {
    await supabase.storage.from(BUCKET_COMPROVANTE).remove([path]);
  }
}